/**
 * Distance Calculator Module
 * Provides geometric calculation functions for task distance tracking
 */

/**
 * Calculate Haversine distance between two lat/lon points
 * @param {number} lat1 - Latitude of point 1 (decimal degrees)
 * @param {number} lon1 - Longitude of point 1 (decimal degrees)
 * @param {number} lat2 - Latitude of point 2 (decimal degrees)
 * @param {number} lon2 - Longitude of point 2 (decimal degrees)
 * @returns {number} Distance in meters
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distance in meters
}

/**
 * Calculate bearing from point 1 to point 2
 * @param {number} lat1 - Latitude of point 1 (decimal degrees)
 * @param {number} lon1 - Longitude of point 1 (decimal degrees)
 * @param {number} lat2 - Latitude of point 2 (decimal degrees)
 * @param {number} lon2 - Longitude of point 2 (decimal degrees)
 * @returns {number} Bearing in degrees (0-360)
 */
export function calculateBearing(lat1, lon1, lat2, lon2) {
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) -
            Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);

  return (θ * 180 / Math.PI + 360) % 360; // Normalize to 0-360
}

/**
 * Normalize angle to -180 to +180 range
 * @param {number} angle - Angle in degrees
 * @returns {number} Normalized angle
 */
export function normalizeAngle(angle) {
  while (angle > 180) angle -= 360;
  while (angle < -180) angle += 360;
  return angle;
}

/**
 * Calculate the projected distance of a point onto a course line
 * This is the "along-track distance" - how far along the course line from the start point
 * the perpendicular projection of the current position falls
 *
 * @param {number} lat1 - Start point latitude (previous TP)
 * @param {number} lon1 - Start point longitude (previous TP)
 * @param {number} lat2 - End point latitude (next TP)
 * @param {number} lon2 - End point longitude (next TP)
 * @param {number} lat3 - Current position latitude
 * @param {number} lon3 - Current position longitude
 * @returns {number} Distance in meters from point 1 along the course line
 */
export function projectedDistance(lat1, lon1, lat2, lon2, lat3, lon3) {
  const R = 6371000; // Earth radius in meters

  // Distance and bearing from A to C (current position)
  const dist_AC = haversineDistance(lat1, lon1, lat3, lon3);
  if (dist_AC < 1) return 0; // At point A

  // Distance from A to B (course line)
  const dist_AB = haversineDistance(lat1, lon1, lat2, lon2);
  if (dist_AB < 1) return 0; // A and B are the same point

  // Bearing from A to C
  const bearing_AC = calculateBearing(lat1, lon1, lat3, lon3);

  // Bearing from A to B (course direction)
  const bearing_AB = calculateBearing(lat1, lon1, lat2, lon2);

  // Angular distance A to C (in radians)
  const δ_AC = dist_AC / R;

  // Difference in bearings (convert to radians)
  const Δθ = normalizeAngle(bearing_AC - bearing_AB) * Math.PI / 180;

  // Cross-track distance (perpendicular distance from course line)
  const sinCrossTrack = Math.sin(δ_AC) * Math.sin(Δθ);
  const δ_XT = Math.asin(Math.max(-1, Math.min(1, sinCrossTrack))); // Clamp for numerical stability

  // Along-track distance (distance along the course line)
  const cosAlongTrack = Math.cos(δ_AC) / Math.cos(δ_XT);
  const δ_AT = Math.acos(Math.max(-1, Math.min(1, cosAlongTrack))); // Clamp for numerical stability

  // Convert back to meters
  const along_track_distance = δ_AT * R;

  // Ensure we don't exceed the total course length, and don't go negative
  return Math.max(0, Math.min(along_track_distance, dist_AB));
}

/**
 * Determine which side of a start LINE a point is on.
 * The start line passes through the sector center, perpendicular to sector.bearing.
 * Returns true if the point is on the "behind" side (opposite to bearing direction),
 * i.e., the side the pilot approaches FROM before crossing.
 *
 * @param {object} point - {lat, lon}
 * @param {object} sector - Must have Lat, Lon, bearing
 * @returns {boolean} True if the point is behind the start line
 */
export function isBehindStartLine(point, sector) {
  const bearingFromCenter = calculateBearing(sector.Lat, sector.Lon, point.lat, point.lon);
  const angleDiff = normalizeAngle(bearingFromCenter - sector.bearing);
  // If angleDiff is within ±90° of the bearing, the point is AHEAD (on the TP1 side)
  // If |angleDiff| > 90, the point is BEHIND the line
  return Math.abs(angleDiff) > 90;
}

/**
 * Check if a track segment (from prevPos to curPos) crosses the start line
 * within the allowed width. Uses interpolation to find the crossing point.
 *
 * For Line=1 starts, the radius is the half-WIDTH of the line, measured
 * perpendicular to the bearing direction (i.e., along the line itself).
 * For sector starts, radius is the cylinder radius (radial distance).
 *
 * @param {object} prevPos - Previous position {lat, lon}
 * @param {object} curPos - Current position {lat, lon}
 * @param {object} sector - Start sector with Lat, Lon, RadiusMeters, bearing, isLine
 * @returns {boolean} True if the segment crosses the start line within radius
 */
export function doesSegmentCrossStartLine(prevPos, curPos, sector) {
  const prevBehind = isBehindStartLine(prevPos, sector);
  const curBehind = isBehindStartLine(curPos, sector);

  // No crossing if both on the same side
  if (prevBehind === curBehind) return false;

  // Must cross from behind to ahead (not ahead to behind)
  if (!prevBehind) return false;

  // Binary search for the crossing point
  let lo = 0, hi = 1;
  for (let i = 0; i < 8; i++) {
    const mid = (lo + hi) / 2;
    const midLat = prevPos.lat + (curPos.lat - prevPos.lat) * mid;
    const midLon = prevPos.lon + (curPos.lon - prevPos.lon) * mid;
    const midBehind = isBehindStartLine({ lat: midLat, lon: midLon }, sector);
    if (midBehind) { lo = mid; } else { hi = mid; }
  }

  const t = (lo + hi) / 2;
  const crossLat = prevPos.lat + (curPos.lat - prevPos.lat) * t;
  const crossLon = prevPos.lon + (curPos.lon - prevPos.lon) * t;

  if (sector.isLine) {
    // For Line starts, use radial distance with a generous multiplier.
    // The R1 value from OZ records is the nominal half-width, but competition
    // scoring often accepts wider crossings. Use 3x R1 to be permissive
    // while still rejecting obviously-wrong distant crossings.
    const crossDist = haversineDistance(crossLat, crossLon, sector.Lat, sector.Lon);
    return crossDist <= sector.RadiusMeters * 3;
  } else {
    // For sector starts, check radial distance from center
    const crossDist = haversineDistance(crossLat, crossLon, sector.Lat, sector.Lon);

    // Also check if either endpoint is within radius (generous check)
    if (crossDist <= sector.RadiusMeters) return true;
    const prevDist = haversineDistance(prevPos.lat, prevPos.lon, sector.Lat, sector.Lon);
    const curDist = haversineDistance(curPos.lat, curPos.lon, sector.Lat, sector.Lon);
    return prevDist <= sector.RadiusMeters || curDist <= sector.RadiusMeters;
  }
}

/**
 * Check if a point is inside a sector (cylinder or partial sector)
 * @param {object} point - {lat, lon} position to check
 * @param {object} sector - Sector definition from task
 * @returns {boolean} True if inside sector
 */
export function isInsideSector(point, sector) {
  const { lat, lon } = point;
  const { Lat, Lon, RadiusMeters, Angle, bearing } = sector;
  const angle = Number(Angle);

  // Calculate distance to sector center
  const distanceToCenter = haversineDistance(lat, lon, Lat, Lon);

  // Check if inside radius
  if (distanceToCenter > RadiusMeters) {
    return false; // Outside radius
  }
  if (distanceToCenter < 1) {
    return true;
  }

  // Full cylinder (360°) - no angle constraint
  if (!Number.isFinite(angle) || Math.abs(angle - 360) < 1) {
    return true;
  }

  // Partial sector - check bearing constraint
  const sectorBearing = Number(bearing);
  if (!Number.isFinite(sectorBearing)) {
    return true;
  }

  // Semicircle/line sectors use the legacy "back side" convention: sector.bearing
  // points out of the sector, so compare from aircraft back to the center.
  if (Math.abs(angle - 180) < 1) {
    const bearingToCenter = calculateBearing(lat, lon, Lat, Lon);
    const angleDiff = Math.abs(normalizeAngle(bearingToCenter - sectorBearing));
    return angleDiff <= 90;
  }

  // FAI/quarter-circle sectors are drawn from the center toward sector.bearing.
  // Match that visible geometry for hit testing.
  const bearingFromCenter = calculateBearing(Lat, Lon, lat, lon);
  const angleDiff = Math.abs(normalizeAngle(bearingFromCenter - sectorBearing));
  return angleDiff <= angle / 2;
}

/**
 * Find the optimal point on a TP cylinder boundary that minimizes total path cost
 * Uses a search approach sampling points around the cylinder
 *
 * @param {object} fromPos - Starting position {lat, lon}
 * @param {object} tp - Current turnpoint definition
 * @param {object} toPos - Target position {lat, lon} (next TP center or finish)
 * @returns {object} Optimal point {lat, lon, distance_m}
 */
export function findOptimalPointOnTP(fromPos, tp, toPos) {
  const { Lat, Lon, RadiusMeters, Angle } = tp;
  const isFullCylinder = Math.abs(Angle - 360) < 1;

  // Distance from starting position to TP center
  const distToCenter_m = haversineDistance(fromPos.lat, fromPos.lon, Lat, Lon);

  // If we're inside the cylinder, optimal point is current position (0 distance)
  if (distToCenter_m <= RadiusMeters) {
    return {
      lat: fromPos.lat,
      lon: fromPos.lon,
      distance_m: 0
    };
  }

  if (!isFullCylinder) {
    // For partial sectors, use center (simplified - could be enhanced)
    return {
      lat: Lat,
      lon: Lon,
      distance_m: distToCenter_m
    };
  }

  // Full cylinder: Sample points around the boundary to find the one that minimizes
  // total distance: fromPos → point → toPos

  const numSamples = 36; // Sample every 10 degrees
  let bestPoint = null;
  let bestTotalDistance = Infinity;

  for (let i = 0; i < numSamples; i++) {
    const bearing = (i * 360 / numSamples) % 360;
    const point = destinationPoint(Lat, Lon, bearing, RadiusMeters);

    // Calculate total path distance through this point
    const distFromPrev = haversineDistance(fromPos.lat, fromPos.lon, point.lat, point.lon);
    const distToNext = haversineDistance(point.lat, point.lon, toPos.lat, toPos.lon);
    const totalDistance = distFromPrev + distToNext;

    if (totalDistance < bestTotalDistance) {
      bestTotalDistance = totalDistance;
      bestPoint = {
        lat: point.lat,
        lon: point.lon,
        distance_m: distFromPrev
      };
    }
  }

  return bestPoint;
}

/**
 * Calculate destination point given start point, bearing, and distance
 * @param {number} lat - Starting latitude
 * @param {number} lon - Starting longitude
 * @param {number} bearing - Bearing in degrees
 * @param {number} distance_m - Distance in meters
 * @returns {object} Destination point {lat, lon}
 */
export function destinationPoint(lat, lon, bearing, distance_m) {
  const R = 6371000; // Earth radius in meters
  const φ1 = lat * Math.PI / 180;
  const λ1 = lon * Math.PI / 180;
  const θ = bearing * Math.PI / 180;
  const δ = distance_m / R;

  const φ2 = Math.asin(
    Math.sin(φ1) * Math.cos(δ) +
    Math.cos(φ1) * Math.sin(δ) * Math.cos(θ)
  );

  const λ2 = λ1 + Math.atan2(
    Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
    Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2)
  );

  return {
    lat: φ2 * 180 / Math.PI,
    lon: λ2 * 180 / Math.PI
  };
}

function isCylinderScoredToWall(tp) {
  return Number(tp?.Angle) > 180 && Number(tp?.RadiusMeters) > 0;
}

export function calculateStartScoredPoint(startTp, nextTp) {
  if (!startTp || !nextTp || !isCylinderScoredToWall(startTp)) {
    return {
      lat: startTp?.Lat,
      lon: startTp?.Lon
    };
  }

  const outboundBearing = calculateBearing(startTp.Lat, startTp.Lon, nextTp.Lat, nextTp.Lon);
  return destinationPoint(startTp.Lat, startTp.Lon, outboundBearing, startTp.RadiusMeters);
}

export function calculateFinishScoredPoint(prevTp, finishTp) {
  if (!prevTp || !finishTp || !isCylinderScoredToWall(finishTp)) {
    return {
      lat: finishTp?.Lat,
      lon: finishTp?.Lon
    };
  }

  const inboundBearing = calculateBearing(finishTp.Lat, finishTp.Lon, prevTp.Lat, prevTp.Lon);
  return destinationPoint(finishTp.Lat, finishTp.Lon, inboundBearing, finishTp.RadiusMeters);
}

export function getTaskPointLocation(tp) {
  const scoredLat = Number(tp?.scoredLat);
  const scoredLon = Number(tp?.scoredLon);

  if (Number.isFinite(scoredLat) && Number.isFinite(scoredLon)) {
    return {
      lat: scoredLat,
      lon: scoredLon
    };
  }

  return {
    lat: tp?.Lat,
    lon: tp?.Lon
  };
}

/**
 * Calculate the minimum geometric distance through remaining TPs from current position
 * Uses dynamic programming to find optimal path through TP boundaries
 *
 * @param {object} currentPos - Current position {lat, lon}
 * @param {array} remainingTPs - Array of remaining turnpoints to visit
 * @returns {number} Minimum geometric distance in meters
 */
export function calculateOptimalRemainingDistance(currentPos, remainingTPs, aircraftId = null) {
  if (remainingTPs.length === 0) {
    return 0;
  }

  if (remainingTPs.length === 1) {
    // Only finish remaining
    const finish = remainingTPs[0];
    const finishPoint = getTaskPointLocation(finish);
    const distToFinish_m = haversineDistance(
      currentPos.lat,
      currentPos.lon,
      finishPoint.lat,
      finishPoint.lon
    );

    return distToFinish_m;
  }

  // Use forward chaining to find optimal path
  // For each TP, find the point on its boundary that minimizes total remaining distance

  let totalDistance_m = 0;
  let currentPoint = currentPos;
  let debugInfo = [];

  // Build the optimal path forward through all remaining TPs
  for (let i = 0; i < remainingTPs.length; i++) {
    const currentTP = remainingTPs[i];
    const nextTP = remainingTPs[i + 1];

    if (!nextTP) {
      // Last TP (finish)
      const finishPoint = getTaskPointLocation(currentTP);
      const distToFinish_m = haversineDistance(
        currentPoint.lat,
        currentPoint.lon,
        finishPoint.lat,
        finishPoint.lon
      );

      totalDistance_m += distToFinish_m;
      debugInfo.push(`${currentTP.Name}: ${(distToFinish_m/1000).toFixed(2)}km (finish)`);
    } else {
      // For intermediate TPs, we need to consider the entire remaining path
      // Find the point on currentTP that minimizes:
      //   distance(currentPoint → point) + optimalRemainingDistance(point → rest of TPs)

      const optimalPoint = findOptimalPointOnTPChain(currentPoint, currentTP, remainingTPs.slice(i + 1));
      totalDistance_m += optimalPoint.distance_m;

      const distToCenter_m = haversineDistance(currentPoint.lat, currentPoint.lon, currentTP.Lat, currentTP.Lon);
      const saved_m = distToCenter_m - optimalPoint.distance_m;
      debugInfo.push(`${currentTP.Name}: ${(optimalPoint.distance_m/1000).toFixed(2)}km (saved ${(saved_m/1000).toFixed(2)}km)`);

      // Next leg starts from this optimal point
      currentPoint = { lat: optimalPoint.lat, lon: optimalPoint.lon };
    }
  }

  // Keep this opt-in only; the per-pilot ETA loop makes it far too noisy in normal replay use.
  if (typeof window !== 'undefined' && window.__CONDORMAP_DEBUG_OPTIMAL_REMAINING === true) {
    const aircraftLabel = aircraftId ? `[${aircraftId}]` : '';
    console.log(`🔍 Optimal remaining calculation ${aircraftLabel}:`);
    debugInfo.forEach(info => console.log(`   ${info}`));
    console.log(`   Total: ${(totalDistance_m/1000).toFixed(2)}km`);
  }

  return totalDistance_m;
}

function estimateFinishDistance(currentPos, finishTp, maximize = false) {
  const finishPoint = getTaskPointLocation(finishTp);
  const distance_m = haversineDistance(
    currentPos.lat,
    currentPos.lon,
    finishPoint.lat,
    finishPoint.lon
  );

  return distance_m;
}

function estimateCenterChainRemainingDistance(fromPos, remainingTPs, maximizeFinish = false) {
  if (!remainingTPs.length) {
    return 0;
  }

  let totalDistance_m = 0;
  let currentPoint = fromPos;

  for (let i = 0; i < remainingTPs.length; i++) {
    const currentTP = remainingTPs[i];
    const isFinish = i === remainingTPs.length - 1;

    if (isFinish) {
      totalDistance_m += estimateFinishDistance(currentPoint, currentTP, maximizeFinish);
      continue;
    }

    totalDistance_m += haversineDistance(
      currentPoint.lat,
      currentPoint.lon,
      currentTP.Lat,
      currentTP.Lon
    );
    currentPoint = { lat: currentTP.Lat, lon: currentTP.Lon };
  }

  return totalDistance_m;
}

function getSectorBoundaryBearings(tp, numSamples) {
  const angle = Number(tp?.Angle);

  if (!Number.isFinite(angle) || angle <= 0 || Math.abs(angle - 360) < 1 || angle > 180) {
    return Array.from({ length: numSamples }, (_, index) => (index * 360 / numSamples) % 360);
  }

  const sectorBearing = Number(tp?.bearing);
  if (!Number.isFinite(sectorBearing)) {
    return [];
  }

  const centerBearing = (sectorBearing + 180) % 360;
  const halfAngle = angle / 2;

  if (numSamples <= 1) {
    return [centerBearing];
  }

  return Array.from({ length: numSamples }, (_, index) => {
    const t = index / (numSamples - 1);
    return (centerBearing - halfAngle + (angle * t) + 360) % 360;
  });
}

function sampleSectorBoundaryPoints(tp, numSamples) {
  const radius_m = Number(tp?.RadiusMeters);

  if (!Number.isFinite(radius_m) || radius_m <= 0) {
    return [{ lat: tp.Lat, lon: tp.Lon }];
  }

  const bearings = getSectorBoundaryBearings(tp, numSamples);
  if (!bearings.length) {
    return [{ lat: tp.Lat, lon: tp.Lon }];
  }

  return bearings.map(bearing => destinationPoint(tp.Lat, tp.Lon, bearing, radius_m));
}

function findMaximumPointOnTPChain(fromPos, tp, remainingTPs) {
  const distToCenter_m = haversineDistance(fromPos.lat, fromPos.lon, tp.Lat, tp.Lon);
  const radius_m = Number(tp?.RadiusMeters);

  if (!Number.isFinite(radius_m) || radius_m <= 0) {
    return {
      lat: tp.Lat,
      lon: tp.Lon,
      distance_m: distToCenter_m
    };
  }

  const numSamples = 36;
  let bestPoint = null;
  let bestTotalCost = -Infinity;
  const candidatePoints = [];

  if (distToCenter_m <= radius_m) {
    candidatePoints.push({
      lat: fromPos.lat,
      lon: fromPos.lon,
      distance_m: 0
    });
  }

  sampleSectorBoundaryPoints(tp, numSamples).forEach(point => {
    candidatePoints.push({
      lat: point.lat,
      lon: point.lon,
      distance_m: haversineDistance(fromPos.lat, fromPos.lon, point.lat, point.lon)
    });
  });

  candidatePoints.forEach(candidate => {
    const remainingCost_m = estimateCenterChainRemainingDistance(
      { lat: candidate.lat, lon: candidate.lon },
      remainingTPs,
      true
    );
    const totalCost_m = candidate.distance_m + remainingCost_m;

    if (totalCost_m > bestTotalCost) {
      bestTotalCost = totalCost_m;
      bestPoint = candidate;
    }
  });

  return bestPoint || {
    lat: tp.Lat,
    lon: tp.Lon,
    distance_m: distToCenter_m
  };
}

export function calculateMaximumRemainingDistance(currentPos, remainingTPs) {
  if (remainingTPs.length === 0) {
    return 0;
  }

  if (remainingTPs.length === 1) {
    return estimateFinishDistance(currentPos, remainingTPs[0], true);
  }

  let totalDistance_m = 0;
  let currentPoint = currentPos;

  for (let i = 0; i < remainingTPs.length; i++) {
    const currentTP = remainingTPs[i];
    const nextTP = remainingTPs[i + 1];

    if (!nextTP) {
      totalDistance_m += estimateFinishDistance(currentPoint, currentTP, true);
      continue;
    }

    const maximumPoint = findMaximumPointOnTPChain(currentPoint, currentTP, remainingTPs.slice(i + 1));
    totalDistance_m += maximumPoint.distance_m;
    currentPoint = { lat: maximumPoint.lat, lon: maximumPoint.lon };
  }

  return totalDistance_m;
}

/**
 * Find optimal point on a TP considering the entire remaining chain of TPs
 * This is more accurate than just considering the next TP
 *
 * @param {object} fromPos - Current position
 * @param {object} tp - Current TP to optimize
 * @param {array} remainingTPs - All TPs after current one
 * @returns {object} Optimal point {lat, lon, distance_m}
 */
function findOptimalPointOnTPChain(fromPos, tp, remainingTPs) {
  const { Lat, Lon, RadiusMeters, Angle } = tp;
  const isFullCylinder = Math.abs(Angle - 360) < 1;

  // Check if inside cylinder
  const distToCenter_m = haversineDistance(fromPos.lat, fromPos.lon, Lat, Lon);
  if (distToCenter_m <= RadiusMeters) {
    return { lat: fromPos.lat, lon: fromPos.lon, distance_m: 0 };
  }

  if (!isFullCylinder) {
    return {
      lat: Lat,
      lon: Lon,
      distance_m: distToCenter_m
    };
  }

  // Sample points around the cylinder
  const numSamples = 36;
  let bestPoint = null;
  let bestTotalCost = Infinity;

  for (let i = 0; i < numSamples; i++) {
    const bearing = (i * 360 / numSamples) % 360;
    const point = destinationPoint(Lat, Lon, bearing, RadiusMeters);

    // Cost = distance to this point + remaining distance from this point
    const distToPoint = haversineDistance(fromPos.lat, fromPos.lon, point.lat, point.lon);

    // Approximate remaining cost using distance to next TP center (faster than full recursion)
    let remainingCost;
    if (remainingTPs.length === 0) {
      remainingCost = 0;
    } else if (remainingTPs.length === 1) {
      // Just finish remaining
      remainingCost = estimateFinishDistance(point, remainingTPs[0]);
    } else {
      // Approximate with center-to-center for remaining TPs (avoids expensive recursion)
      const nextTP = remainingTPs[0];
      remainingCost = haversineDistance(point.lat, point.lon, nextTP.Lat, nextTP.Lon);
      for (let j = 1; j < remainingTPs.length; j++) {
        remainingCost += haversineDistance(
          remainingTPs[j - 1].Lat,
          remainingTPs[j - 1].Lon,
          remainingTPs[j].Lat,
          remainingTPs[j].Lon
        );
      }
    }

    const totalCost = distToPoint + remainingCost;

    if (totalCost < bestTotalCost) {
      bestTotalCost = totalCost;
      bestPoint = {
        lat: point.lat,
        lon: point.lon,
        distance_m: distToPoint
      };
    }
  }

  return bestPoint;
}

/**
 * Calculate the total optimal task distance (net distance with perfect TP touches)
 * This is the minimum geometric distance from start through all TPs to finish
 * @param {array} turnpoints - Array of all turnpoints
 * @returns {number} Optimal task distance in kilometers
 */
export function calculateOptimalTaskDistance(turnpoints) {
  if (turnpoints.length < 2) {
    return 0;
  }

  const startTP = turnpoints[0];
  let currentPoint = getTaskPointLocation(startTP);
  let totalDistance_m = 0;

  // Route through all turnpoints to finish
  for (let i = 1; i < turnpoints.length; i++) {
    const currentTP = turnpoints[i];
    const nextTP = turnpoints[i + 1];

    if (!nextTP) {
      totalDistance_m += estimateFinishDistance(currentPoint, currentTP);
    } else {
      // Find optimal point on current TP that minimizes distance to next TP
      const optimalPoint = findOptimalPointOnTP(currentPoint, currentTP, getTaskPointLocation(nextTP));
      totalDistance_m += optimalPoint.distance_m;

      // Next leg starts from this optimal point
      currentPoint = { lat: optimalPoint.lat, lon: optimalPoint.lon };
    }
  }
  return totalDistance_m / 1000; // Convert to km
}

/**
 * Calculate turnpoint bonus distance (Extended-Spectate algorithm)
 * @param {number} tpIndex - Turnpoint index
 * @param {array} turnpoints - Array of all turnpoints
 * @returns {number} Bonus distance in kilometers
 */
export function calculateTurnpointBonus(tpIndex, turnpoints) {
  const tp = turnpoints[tpIndex];
  const radiusKm = tp.RadiusMeters / 1000;

  // Check if full cylinder (360°)
  const isFullCylinder = Math.abs(tp.Angle - 360) < 1;

  // Start and finish are already scored to their fixed start/finish wall points.
  if (tpIndex === 0) {
    return 0;
  }

  if (tpIndex === turnpoints.length - 1) {
    return 0;
  }

  // Intermediate turnpoints: geometric bonus
  if (isFullCylinder) {
    // Need previous and next turnpoints to calculate turn angle
    if (tpIndex < 1 || tpIndex >= turnpoints.length - 1) {
      return 0; // Edge case - shouldn't happen
    }

    const prevTP = turnpoints[tpIndex - 1];
    const nextTP = turnpoints[tpIndex + 1];

    const bearingIn = calculateBearing(prevTP.Lat, prevTP.Lon, tp.Lat, tp.Lon);
    const bearingOut = calculateBearing(tp.Lat, tp.Lon, nextTP.Lat, nextTP.Lon);

    const turnAngle = Math.abs(normalizeAngle(bearingOut - bearingIn));
    const effectiveTurnAngle = Math.min(turnAngle, 360 - turnAngle);

    const bonus = 2 * radiusKm * Math.sin(effectiveTurnAngle / 2 * Math.PI / 180);
    return bonus;
  }

  return 0; // Non-cylinder turnpoints get no bonus
}
